import { useState } from "react";
import { Car, Search, Loader2, CheckCircle2, AlertCircle } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { toast } from "sonner";
import { authFetch } from "../services/authService";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function CustomerVehicleSelector({
  customerId,
  vehicleData,
  onVehicleChange,
  required = false
}) {
  // Vehicle lookup state
  const [lookupLoading, setLookupLoading] = useState(false);
  const [lookupResult, setLookupResult] = useState(null); // 'found', 'not_found', null

  // Normalize fuel type from API to Italian
  const normalizeFuelType = (fuelType) => {
    if (!fuelType) return "";
    const fuelMap = {
      'gasoline': 'Benzina',
      'diesel': 'Diesel',
      'gasolio': 'Diesel',
      'gpl': 'GPL',
      'lpg': 'GPL',
      'methane': 'Metano',
      'metano': 'Metano',
      'cng': 'Metano',
      'electric': 'Elettrico',
      'elettrico': 'Elettrico',
      'hybrid': 'Ibrido Benzina',
      'ibrido': 'Ibrido Benzina',
      'hybrid_petrol': 'Ibrido Benzina',
      'hybrid_diesel': 'Ibrido Diesel',
      'benzina': 'Benzina'
    };
    return fuelMap[fuelType.toLowerCase()] || fuelType;
  };

  // Vehicle lookup from tuttotarghe.it API
  const handleVehicleLookup = async () => {
    const plate = vehicleData.vehicle_plate?.replace(/[^A-Za-z0-9]/g, '').toUpperCase();
    
    if (!plate || plate.length < 5) {
      toast.error("Inserisci una targa valida (minimo 5 caratteri)");
      return;
    }

    setLookupLoading(true);
    setLookupResult(null);

    try {
      const response = await authFetch(`${API_URL}/api/vehicles/lookup/${plate}`);

      if (response.ok) {
        const data = await response.json();
        setLookupResult('found');
        
        // Auto-populate form fields with found data
        onVehicleChange({
          vehicle_plate: data.licensePlate || plate,
          vehicle_brand: data.brand || "",
          vehicle_model: data.model || "",
          vehicle_version: data.modelDetail || "",
          vehicle_fuel_type: normalizeFuelType(data.fuelType) || "",
          vehicle_cubic_capacity: data.cubicCapacity?.toString() || "",
          vehicle_power_kw: data.power_kw?.toString() || "",
          vehicle_power_hp: data.power_hp?.toString() || "",
          vehicle_first_registration: data.firstRegistrationDate || "",
          vehicle_engine_code: vehicleData.vehicle_engine_code || "",
          vehicle_vin: vehicleData.vehicle_vin || "",
          vehicle_km: vehicleData.vehicle_km || "",
          vehicle_year: data.buildYear?.toString() || ""
        });
        
        const sourceText = data.source === 'cache' ? 'archivio locale' : 'TuttoTarghe';
        toast.success(`Dati veicolo caricati (${sourceText})`);
      } else if (response.status === 404) {
        setLookupResult('not_found');
        toast.info("Veicolo non trovato - inserisci i dati manualmente");
      } else {
        setLookupResult('not_found');
        toast.error("Errore nella ricerca");
      }
    } catch (error) {
      console.error("Lookup error:", error);
      setLookupResult('not_found');
      toast.error("Errore di connessione");
    } finally {
      setLookupLoading(false);
    }
  };

  // Update parent when local fields change
  const handleFieldChange = (field, value) => {
    // Reset lookup status when plate changes
    if (field === 'vehicle_plate') {
      setLookupResult(null);
    }
    onVehicleChange(prev => ({
      ...prev,
      [field]: value
    }));
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <h4 className="text-primary uppercase text-sm font-bold flex items-center gap-2">
        <Car className="w-4 h-4" /> Dati Veicolo
      </h4>

      {/* Row 1: Targa con Ricerca */}
      <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
        <div className="md:col-span-4 form-group">
          <label className="form-label">Targa {required && "*"}</label>
          <div className="flex gap-2">
            <Input
              type="text"
              className="bg-white border-zinc-300 text-zinc-900 placeholder:text-zinc-400 uppercase font-mono flex-1"
              placeholder="AA123BB"
              value={vehicleData.vehicle_plate || ""}
              onChange={(e) => handleFieldChange("vehicle_plate", e.target.value.toUpperCase())}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleVehicleLookup();
                }
              }}
              required={required}
              data-testid="vehicle-plate-input"
            />
            <Button
              type="button"
              onClick={handleVehicleLookup}
              disabled={lookupLoading || !vehicleData.vehicle_plate || vehicleData.vehicle_plate.length < 5}
              className="btn-primary px-4"
              data-testid="vehicle-lookup-btn"
            >
              {lookupLoading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <>
                  <Search className="w-4 h-4 mr-1" />
                  Cerca
                </>
              )}
            </Button>
          </div>
          {/* Lookup Status Indicator */}
          {lookupResult && (
            <div className={`flex items-center gap-1 mt-1 text-xs ${lookupResult === 'found' ? 'text-green-500' : 'text-yellow-500'}`}>
              {lookupResult === 'found' ? (
                <>
                  <CheckCircle2 className="w-3 h-3" />
                  <span>Dati caricati</span>
                </>
              ) : (
                <>
                  <AlertCircle className="w-3 h-3" />
                  <span>Non trovato - compila manualmente</span>
                </>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Row 2: Marca, Modello, Allestimento */}
      <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
        {/* Marca */}
        <div className="md:col-span-4 form-group">
          <label className="form-label">Marca {required && "*"}</label>
          <Input
            type="text"
            className="bg-white border-zinc-300 text-zinc-900 placeholder:text-zinc-400"
            placeholder="es. Alfa Romeo"
            value={vehicleData.vehicle_brand || ""}
            onChange={(e) => handleFieldChange("vehicle_brand", e.target.value)}
            required={required}
            data-testid="vehicle-brand-input"
          />
        </div>

        {/* Modello */}
        <div className="md:col-span-4 form-group">
          <label className="form-label">Modello {required && "*"}</label>
          <Input
            type="text"
            className="bg-white border-zinc-300 text-zinc-900 placeholder:text-zinc-400"
            placeholder="es. Stelvio"
            value={vehicleData.vehicle_model || ""}
            onChange={(e) => handleFieldChange("vehicle_model", e.target.value)}
            required={required}
            data-testid="vehicle-model-input"
          />
        </div>

        {/* Allestimento */}
        <div className="md:col-span-4 form-group">
          <label className="form-label">Allestimento</label>
          <Input
            type="text"
            className="bg-white border-zinc-300 text-zinc-900 placeholder:text-zinc-400"
            placeholder="es. 2.2 TD 210CV Q4"
            value={vehicleData.vehicle_version || ""}
            onChange={(e) => handleFieldChange("vehicle_version", e.target.value)}
            data-testid="vehicle-version-input"
          />
        </div>
      </div>

      {/* Row 3: Alimentazione, Cilindrata, Potenza KW, Potenza HP */}
      <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
        {/* Alimentazione */}
        <div className="md:col-span-3 form-group">
          <label className="form-label">Alimentazione</label>
          <Input
            type="text"
            className="bg-white border-zinc-300 text-zinc-900 placeholder:text-zinc-400"
            placeholder="es. Diesel"
            value={vehicleData.vehicle_fuel_type || ""}
            onChange={(e) => handleFieldChange("vehicle_fuel_type", e.target.value)}
            data-testid="vehicle-fuel-type-input"
          />
        </div>

        {/* Cilindrata */}
        <div className="md:col-span-3 form-group">
          <label className="form-label">Cilindrata (cc)</label>
          <Input
            type="text"
            className="bg-white border-zinc-300 text-zinc-900 placeholder:text-zinc-400 font-mono"
            placeholder="es. 2143"
            value={vehicleData.vehicle_cubic_capacity || ""}
            onChange={(e) => handleFieldChange("vehicle_cubic_capacity", e.target.value)}
            data-testid="vehicle-cubic-capacity-input"
          />
        </div>

        {/* Potenza KW */}
        <div className="md:col-span-3 form-group">
          <label className="form-label">Potenza (kW)</label>
          <Input
            type="text"
            className="bg-white border-zinc-300 text-zinc-900 placeholder:text-zinc-400 font-mono"
            placeholder="es. 154"
            value={vehicleData.vehicle_power_kw || ""}
            onChange={(e) => handleFieldChange("vehicle_power_kw", e.target.value)}
            data-testid="vehicle-power-kw-input"
          />
        </div>

        {/* Potenza HP */}
        <div className="md:col-span-3 form-group">
          <label className="form-label">Potenza (CV)</label>
          <Input
            type="text"
            className="bg-white border-zinc-300 text-zinc-900 placeholder:text-zinc-400 font-mono"
            placeholder="es. 210"
            value={vehicleData.vehicle_power_hp || ""}
            onChange={(e) => handleFieldChange("vehicle_power_hp", e.target.value)}
            data-testid="vehicle-power-hp-input"
          />
        </div>
      </div>

      {/* Row 4: Prima Immatricolazione, Codice Motore, VIN, Chilometraggio */}
      <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
        {/* Prima Immatricolazione */}
        <div className="md:col-span-3 form-group">
          <label className="form-label">Prima Immatricolazione</label>
          <Input
            type="text"
            className="bg-white border-zinc-300 text-zinc-900 placeholder:text-zinc-400"
            placeholder="es. 2020-03-15"
            value={vehicleData.vehicle_first_registration || ""}
            onChange={(e) => handleFieldChange("vehicle_first_registration", e.target.value)}
            data-testid="vehicle-first-registration-input"
          />
        </div>

        {/* Codice Motore */}
        <div className="md:col-span-3 form-group">
          <label className="form-label">Codice Motore</label>
          <Input
            type="text"
            className="bg-white border-zinc-300 text-zinc-900 placeholder:text-zinc-400 uppercase font-mono"
            placeholder="es. 169A4000"
            value={vehicleData.vehicle_engine_code || ""}
            onChange={(e) => handleFieldChange("vehicle_engine_code", e.target.value.toUpperCase())}
            data-testid="vehicle-engine-code-input"
          />
        </div>

        {/* VIN/Telaio */}
        <div className="md:col-span-3 form-group">
          <label className="form-label">VIN / Telaio</label>
          <Input
            type="text"
            className="bg-white border-zinc-300 text-zinc-900 placeholder:text-zinc-400 uppercase font-mono text-xs"
            placeholder="WVWZZZ3CZWE123456"
            value={vehicleData.vehicle_vin || ""}
            onChange={(e) => handleFieldChange("vehicle_vin", e.target.value.toUpperCase())}
            data-testid="vehicle-vin-input"
          />
        </div>

        {/* Chilometraggio */}
        <div className="md:col-span-3 form-group">
          <label className="form-label">Chilometraggio</label>
          <Input
            type="text"
            className="bg-white border-zinc-300 text-zinc-900 placeholder:text-zinc-400 font-mono"
            placeholder="es. 85000"
            value={vehicleData.vehicle_km || ""}
            onChange={(e) => handleFieldChange("vehicle_km", e.target.value)}
            data-testid="vehicle-km-input"
          />
        </div>
      </div>
    </div>
  );
}
